// F7: how a notification row reads in the bell and in an OS notification. Payloads are
// display-only (title / link / names) — never transcript text (FR-M7).
import { trashPath } from "../library/routes";
import type { NotificationOut } from "./api";

function str(payload: Record<string, unknown>, key: string): string {
  const v = payload[key];
  return typeof v === "string" ? v : "";
}

export interface NotificationText {
  title: string;
  body: string;
  /** Who it is from, for the Avatar; "Grackle" for system events. */
  actor: string;
}

export function notificationText(n: Pick<NotificationOut, "kind" | "payload">): NotificationText {
  const p = n.payload ?? {};
  const title = str(p, "title") || "Untitled recording";
  switch (n.kind) {
    case "recording.shared_with_you": {
      const who = str(p, "sharer_name") || "Someone";
      return { title: `${who} shared a recording with you`, body: title, actor: who };
    }
    case "recording.ready":
      return { title: "Your recording is ready", body: title, actor: "Grackle" };
    case "transcript.ready":
      return { title: "Transcript ready", body: title, actor: "Grackle" };
    case "meeting.notes_ready":
      return { title: "Meeting notes are ready", body: title, actor: "Grackle" };
    case "share_link.view_limit_reached":
      return { title: "A share link reached its view limit", body: title, actor: "Grackle" };
    case "recording.retention_scheduled": {
      const when = str(p, "action_at");
      const date = when ? new Date(when).toLocaleDateString() : "soon";
      const verb = str(p, "action") === "delete" ? "deleted" : "moved to trash";
      return { title: `A retention policy will act on your recording`, body: `${title} will be ${verb} on ${date}`, actor: "Grackle" };
    }
    case "org.invite": {
      const org = str(p, "org_name") || "an organization";
      return { title: `You're invited to ${org}`, body: "Open to join", actor: org };
    }
    case "quota.threshold": {
      const what = str(p, "threshold");
      const head =
        what === "hard"
          ? "stopped public viewing"
          : what === "soft"
            ? "passed its viewing limit"
            : "is at 80% of its viewing limit";
      return { title: `${title} ${head}`, body: "Open to review usage", actor: "Grackle" };
    }
    default:
      return { title: "New notification", body: title, actor: "Grackle" };
  }
}

/** The Phosphor icon for a notification kind (the DS NotificationsScreen's 32px lavender disc). */
export function notificationIcon(kind: string): string {
  switch (kind) {
    case "recording.ready":
      return "play";
    case "transcript.ready":
      return "subtitles";
    case "recording.shared_with_you":
      return "users-three";
    case "meeting.notes_ready":
      return "file-text";
    case "share_link.view_limit_reached":
    case "quota.threshold":
      return "warning-circle";
    case "org.invite":
      return "envelope-simple";
    case "recording.retention_scheduled":
      return "clock";
    default:
      return "bell";
  }
}

/** Where clicking a notification goes: its link while the recording is live, the org's trash
 * once its owner has trashed it (the link would show "not here"), and nowhere once it is
 * deleted. */
export function notificationPath(n: Pick<NotificationOut, "payload" | "recording">): string | null {
  if (n.recording?.state === "trashed") return trashPath(n.recording.org_slug);
  if (n.recording?.state === "deleted") return null;
  return internalPath(n.payload?.link);
}

/** The in-app route for a notification's absolute `link` (the desktop webview's origin is not
 * the API host, so only the path is navigated). Null when there is nothing to open. */
export function internalPath(link: unknown): string | null {
  if (typeof link !== "string" || !link) return null;
  try {
    const u = new URL(link, "http://placeholder.invalid");
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return null;
  }
}
